import React, { useCallback } from 'react';

import StepNavigation from 'shared/components/StepNavigation';
import { useScrollArea } from 'shared/context/ScrollAreaContext';
import { afterPaint } from 'shared/lib/scroll';
import { useStepNavValue } from 'features/onboarding/context/StepNavContext';

/**
 * Fixed footer for the onboarding form column. Renders the current step's
 * Back / Next controls from the StepNav context. Returns null when no step has
 * published navigation, so the footer bar only appears when there is something
 * to show.
 */
const StepFooter: React.FC = () => {
  const nav = useStepNavValue();
  const { scrollToFirstError } = useScrollArea();

  // A blocked step renders its validation errors on the next paint; bringing
  // the first invalid field into view saves the partner from hunting for it.
  // When the step does advance there is nothing to match and this is a no-op.
  const handleNext = useCallback(async () => {
    await nav?.onNext?.();
    afterPaint(() => scrollToFirstError());
  }, [nav, scrollToFirstError]);

  if (!nav || (!nav.onNext && !nav.onBack)) return null;

  return (
    <div className="shrink-0 border-t border-gray-200 bg-white/85 px-5 py-4 backdrop-blur sm:px-8">
      <StepNavigation
        className="w-full"
        nextType="button"
        onBack={nav.onBack}
        onNext={nav.onNext ? handleNext : undefined}
        nextLabel={nav.nextLabel}
        isSubmitting={nav.isSubmitting}
        hideBack={nav.hideBack}
        hideNext={nav.hideNext}
        nextDisabled={nav.nextDisabled}
      />
    </div>
  );
};

export default StepFooter;
